<template>
  <TText size="lg" tag="h1" variant="semibold" classes="mb-4">{{
    t('views.registration-owner.title')
  }}</TText>
  <div v-if="currentState === 'ERROR'" class="my-4">
    <ApiResponseError :api-response-error="apiResponseError" />
  </div>
  <UForm
    ref="form"
    :schema="schema"
    :state="state"
    class="space-y-3"
    @submit="onSubmit"
  >
    <UFormField required name="phone">
      <UInput
        v-model="state.phone"
        size="lg"
        type="tel"
        :placeholder="`${t('auth.register.phone')}`"
        :aria-label="`${t('auth.register.phone')}`"
        :title="t('auth.register.phone-title')"
        autocomplete="on"
      />
    </UFormField>
    <UFormField required name="emailAddress">
      <UInput
        v-model="state.emailAddress"
        type="email"
        size="lg"
        :placeholder="t('auth.register.email')"
        :aria-label="t('auth.register.email')"
        :title="t('auth.register.email-title')"
        autocomplete="on"
      />
    </UFormField>
    <UFormField required name="password">
      <TPassword
        v-model="state.password"
        :placeholder="t('auth.register.password')"
        :aria-label="t('auth.register.password')"
        :title="t('auth.register.password-title')"
        autocomplete="on"
      />
    </UFormField>
    <UFormField name="privacyPolicy">
      <UCheckbox v-model="state.privacyPolicy" required>
        <template #label>
          <i18n-t keypath="auth.confirmation.label" tag="span" scope="global">
            <template #useConditions>
              <ULink
                :active="true"
                target="_blank"
                :href="t('auth.confirmation.useConditionsUrl')"
                >{{ t('auth.confirmation.useConditions') }}</ULink
              >
            </template>
            <template #privacyPolicy>
              <ULink
                :active="true"
                target="_blank"
                :href="t('auth.confirmation.privacyPolicyUrl')"
                >{{ t('auth.confirmation.privacyPolicy') }}</ULink
              >
            </template>
          </i18n-t>
        </template>
      </UCheckbox>
    </UFormField>
    <UFormField name="recaptcha">
      <TRecaptcha
        ref="vueRecaptchaRef"
        v-model="state.recaptcha"
        class="mt-6 mb-3"
      />
    </UFormField>

    <div class="flex flex-col items-center">
      <UButton
        class="mt-2 mb-6"
        color="neutral"
        variant="solid"
        size="xl"
        type="submit"
        :disabled="!isValid"
        :loading="currentState === 'SENDING'"
      >
        {{ t('auth.register.submit') }}
      </UButton>
    </div>
  </UForm>
</template>

<script setup lang="ts">
import { z } from 'zod'
import { browserFetch as fetchSignUp } from '~~/modules/api/rurality/v3/users/sign-up'
import { browserFetch as fetchLogin } from '~~/modules/api/rurality/v3/users/login'
import { paramsSchema } from '~~/shared/api/rurality/v3/users/sign-up/paramsSchema'
import { useZodValidator } from '~~/modules/form/composables/useZodValidator'
import { scrollToTop } from '~/composables/useAnimations'
import { signUpOrigin } from '~~/modules/auth/constants'
import loggedUserRedirect from '~~/modules/auth/composables/useAuthRedirect'
import { sendGa4RegisterOwnerEvent } from '~~/modules/integrations/ga4/composables/useAnalyticsGa4Composable'

interface ChildComponent {
  triggerReset(): void
}

const apiResponseError: Ref<ApiResponseError | null> = ref(null)
const vueRecaptchaRef = ref<ChildComponent | null>(null)

const recaptchaReset = () => {
  state.recaptcha = undefined
  vueRecaptchaRef.value?.triggerReset()
}

const { validatePhone, validatePassword, validateRequiredCheckbox } =
  useZodValidator()

const state = reactive({
  firstName: 'Propietario',
  lastName: null,
  phone: undefined,
  emailAddress: undefined,
  emailAddressRepeat: undefined,
  password: undefined,
  provinceId: null,
  allowComms: false,
  privacyPolicy: undefined,
  recaptcha: undefined,
  facebookId: null,
  googleId: null,
  appleId: null,
  userType: 'OWNER' as const,
  signUpOrigin: signUpOrigin.OWNER_SIGNUP_PAGE,
})

const form = ref()

const schema = paramsSchema.extend({
  phone: validatePhone()
    .transform(value => (value === '' ? null : value))
    .transform(value => (value === undefined ? null : value)),
  emailAddress: z.string().email().min(5).max(254),
  password: validatePassword(),
  privacyPolicy: validateRequiredCheckbox(),
})

const states = {
  IDLE: 'IDLE',
  INVALID: 'INVALID',
  VALID: 'VALID',
  SENDING: 'SENDING',
  ERROR: 'ERROR',
}

const currentState: Ref<keyof typeof states> = ref('IDLE')

const isValid = computed(() => {
  return schema.safeParse(state).success
})

watch(
  () => state.emailAddress,
  newValue => {
    state.emailAddressRepeat = newValue
  },
)

async function onSubmit() {
  const result = schema.safeParse(state)

  if (result.success) {
    currentState.value = 'SENDING'

    const parsedData = result.data

    const signUp = await fetchSignUp(parsedData)

    if (signUp.success && signUp.success.data) {
      const login = await fetchLogin({
        emailAddress: parsedData.emailAddress,
        password: parsedData.password,
      })
      if (login.success && login.success.data) {
        const urlRedirect = loggedUserRedirect({
          userType: login.success.data.userType,
          userId: login.success.data.userId,
          isOwnerRegister: true,
        })
        sendGa4RegisterOwnerEvent({
          eventCallback: () => {
            navigateTo(urlRedirect, { external: true })
          },
        })
      } else {
        apiResponseError.value = login.error
        currentState.value = 'ERROR'
        scrollToTop()
        recaptchaReset()
      }
    } else {
      apiResponseError.value = signUp.error
      currentState.value = 'ERROR'
      scrollToTop()
      recaptchaReset()
    }
  }
}
</script>
